import { Factory, trait } from 'miragejs';
import moment from 'moment-timezone';
import { App } from 'garaje/utils/enums';

const VISITORS_STANDARD_FEATURES = [
  'nda_bcc',
  'nda_enabled',
  'sign_in_visitors',
  'slack',
  'visitor_badge_printing',
  'visitor_survey',
  'welcome_guide',
];
const VISITORS_PREMIUM_FEATURES = [
  ...VISITORS_STANDARD_FEATURES,
  'analytics',
  'assistants',
  'capacity_limits',
  'conditional_alerts',
  'conditional_sign_in_fields',
  'contact_tracing',
  'custom_badge',
  'custom_notifications',
  'directory_sync',
  'global_analytics',
  'global_sign_in_flows',
  'group_sign_in',
  'id_check',
  'locations_grouping',
  'multiple_hosts',
  'multiple_visitor_types',
  'pre_registration_required',
  'presign_nda',
  'saml',
  'scheduled_reports',
  'sign_in_from_invite',
  'slideshows',
  'sms_notification',
  'video_in_legal_document',
  'visitor_photos',
  'static_qr_codes',
  'group_invites',
];
const VISITORS_ENTERPRISE_FEATURES = [
  ...VISITORS_PREMIUM_FEATURES,
  'blacklist',
  'id_scanning',
  'invite_approvals',
  'multi_location_invites',
  'multiple_printers',
  'virtual_front_desk',
  'visual_compliance',
  'facial_recognition',
  'host_search_options',
];

const PROTECT_LEGACY_VISITORS_STANDARD = [...VISITORS_STANDARD_FEATURES, 'protect_legacy_features'];
const PROTECT_LEGACY_VISITORS_PREMIUM = [...VISITORS_PREMIUM_FEATURES, 'protect_legacy_features'];

const WORKPLACE_STANDARD_FEATURES = [
  'manual_employee_directory',
  'doors_by_location',
  'favorite_coworkers',
  'workplace_scheduling',
  'mobile_manual_sign_in',
  'mobile_gps_auto_sign_in',
  'static_maps',
  'maps_point_of_interest',
  'static_seating_charts',
  'ticketing',
  'mobile_announcements',
  'configurable_advance_scheduling_window',
  'employee_analytics',
  'capacity_limits',
];
const WORKPLACE_PREMIUM_FEATURES = [
  ...WORKPLACE_STANDARD_FEATURES,
  'access_control_auto_sign_in',
  'pre_registration_questionaire',
  'occupancy_analytics',
  'scim_employee_directory_sync',
  'sso',
  'capacity_limits',
  'vaccination_upload_and_approval',
  'testing_upload_and_approval',
  'attendance_analytics',
];

export { VISITORS_PREMIUM_FEATURES };

export default Factory.extend({
  employeeLimit: 'Infinity',
  locationLimit: 'Infinity',
  app: null,
  timesTrialExtended: 0,
  plan: null,
  features: () => [],

  asDesksOnTrial: trait({
    app: App.DESKS,
    subscribed: false,
    onTrial: true,
    trialEndDate: moment().add(8, 'days').toDate(),
  }),

  asDesksTrialExpired: trait({
    app: App.DESKS,
    subscribed: false,
    onTrial: false,
    onExpiredTrial: true,
    trialEndDate: moment().subtract(8, 'days').toDate(),
  }),

  asDesksSubscribed: trait({
    app: App.DESKS,
    subscribed: true,
    plan: 'standard',
  }),

  asDesksBasic: trait({
    app: App.DESKS,
    subscribed: true,
    plan: 'basic',
  }),

  asDesksMonthlySubscribedRenewsInOneWeek: trait({
    plan: 'standard',
    quantity: 2,
    period: 'monthly',
    nextPeriod: 'monthly',
    periodEnd: moment().add(7, 'day').unix(),
  }),

  asStandard: trait({
    subscribed: true,
    plan: 'standard',
  }),

  asPremium: trait({
    subscribed: true,
    plan: 'premium',
  }),

  asEnterprise: trait({
    subscribed: true,
    plan: 'enterprise',
  }),

  asTrial: trait({
    subscribed: false,
    onTrial: true,
    onExpiredTrial: false,
    trialEndDate: moment().add(8, 'days').toDate(),
  }),

  asTrialExpired: trait({
    subscribed: false,
    onTrial: true,
    onExpiredTrial: true,
    cancelled: true,
    trialEndDate: moment().subtract(8, 'days').toDate(),
  }),

  asTrialExtended: trait({
    subscribed: false,
    onTrial: true,
    onExpiredTrial: false,
    timesTrialExtended: 1,
    trialEndDate: moment().add(8, 'days').toDate(),
  }),

  asTrialExtendedAndExpired: trait({
    subscribed: false,
    onTrial: true,
    onExpiredTrial: true,
    timesTrialExtended: 1,
    trialEndDate: moment().subtract(8, 'days').toDate(),
  }),

  asDeliveriesOnTrial: trait({
    app: App.DELIVERIES,
    subscribed: false,
    onTrial: true,
    trialEndDate: moment().add(8, 'days').toDate(),
  }),

  asDeliveriesStandard: trait({
    app: App.DELIVERIES,
    plan: 'standard',
  }),

  asVisitorsBasic: trait({
    app: App.VISITORS,
    plan: 'basic',
    subscribed: true,
    onExpiredTrial: false,
    onTrial: false,

    afterCreate(subscription /* , server */) {
      subscription.company?.update({ subscription });
    },
  }),

  asVisitorsCancelled: trait({
    app: App.VISITORS,
    plan: 'standard',
    subscribed: false,
    cancelled: true,

    afterCreate(subscription /* , server */) {
      subscription.company?.update({ subscription });
    },
  }),

  asVisitorsStandard: trait({
    app: App.VISITORS,
    plan: 'standard-1',
    subscribed: true,
    onExpiredTrial: false,
    onTrial: false,
    nonRenewing: false,
    features: VISITORS_STANDARD_FEATURES,

    afterCreate(subscription /* , server */) {
      subscription.company?.update({ subscription });
    },
  }),

  asVisitorsPremium: trait({
    app: App.VISITORS,
    plan: 'premium',
    subscribed: true,
    onTrial: false,
    onExpiredTrial: false,
    nonRenewing: false,
    features: VISITORS_PREMIUM_FEATURES,

    afterCreate(subscription /* , server */) {
      subscription.company?.update({ subscription });
    },
  }),

  asVisitorsEnterprise: trait({
    app: App.VISITORS,
    plan: 'enterprise',
    subscribed: true,
    onExpiredTrial: false,
    onTrial: false,
    nonRenewing: false,
    features: VISITORS_ENTERPRISE_FEATURES,

    afterCreate(subscription /* , server */) {
      subscription.company?.update({ subscription });
    },
  }),

  asVisitorsEnterpriseOnTrial: trait({
    app: App.VISITORS,
    plan: 'enterprise',
    subscribed: true,
    onExpiredTrial: false,
    onTrial: true,
    trialEndDate: moment().add(3, 'days').toDate(),
    nonRenewing: false,
    features: VISITORS_ENTERPRISE_FEATURES,

    afterCreate(subscription /* , server */) {
      subscription.company?.update({ subscription });
    },
  }),

  asVisitorsStandardProtectLegacy: trait({
    app: App.VISITORS,
    plan: 'premium',
    subscribed: true,
    onTrial: false,
    onExpiredTrial: false,
    nonRenewing: false,
    features: PROTECT_LEGACY_VISITORS_STANDARD,
  }),

  asVisitorsPremiumProtectLegacy: trait({
    app: App.VISITORS,
    plan: 'premium',
    subscribed: true,
    onTrial: false,
    onExpiredTrial: false,
    nonRenewing: false,
    features: PROTECT_LEGACY_VISITORS_PREMIUM,
  }),

  withVisitorsFeatures: trait({
    features: [
      'pre_registration_required',
      'billing',
      'sign_in_visitors',
      'custom_notifications',
      'group_sign_in',
      'nda_enabled',
      'visitor_photos',
      'visitor_badge_printing',
      'sms_notification',
      'slideshows',
      'custom_badge',
      'presign_nda',
      'multiple_visitor_types',
      'sign_in_from_invite',
      'slack',
      'analytics',
      'assistants',
      'blacklist',
      'locations_grouping',
      'global_analytics',
      'rooms-calendar-permissions',
    ],
  }),

  asWorkplaceStandard: trait({
    app: App.WORKPLACE,
    plan: 'standard',
    subscribed: true,
    onExpiredTrial: false,
    onTrial: false,
    nonRenewing: false,
    features: WORKPLACE_STANDARD_FEATURES,
  }),

  asWorkplacePremium: trait({
    app: App.WORKPLACE,
    plan: 'premium',
    subscribed: true,
    onTrial: false,
    onExpiredTrial: false,
    nonRenewing: false,
    features: WORKPLACE_PREMIUM_FEATURES,
  }),

  asWorkplacePremiumPlus: trait({
    app: App.WORKPLACE,
    plan: 'premium-plus',
    subscribed: true,
    onTrial: false,
    onExpiredTrial: false,
    nonRenewing: false,
    features: WORKPLACE_PREMIUM_FEATURES,
  }),

  asConnect: trait({
    app: App.CONNECT,
    plan: 'early-access',
    subscribed: true,
    onTrial: false,
    onExpiredTrial: false,
    nonRenewing: false,
    nextPeriod: 'yearly',
    features: ['analytics'],
  }),

  asVfd: trait({
    app: App.VFD,
    plan: 'standard',
    subscribed: true,
    onTrial: false,
    onExpiredTrial: false,
    nonRenewing: false,
    features: ['virtual_front_desk'],
  }),

  asRoomsSubscribed: trait({
    app: App.ROOMS,
    subscribed: true,
    plan: 'standard',
  }),
});
